import React, { useState, useEffect } from 'react';
import { FaCheck, FaRegCopy } from 'react-icons/fa';

import { getCurrentSaleConfig } from 'constants/topBannerConfig';
import { useActiveParityOffer } from 'context/ParityOfferContext';
import CountryFlag from 'components/CountryFlag';
import { getParityOfferCopy } from 'utils/shared/parityOffers';
import { PARITY_CODE_COPIED } from 'services/client/analytics/events';
import { trackEvent } from 'services/client/analytics';
import { copyText } from 'utils/client/copyText';

const PromoButton = () => {
  const [copied, setCopied] = useState(false);

  const saleConfig = getCurrentSaleConfig();
  const BUTTON_TEXT = saleConfig.buttonText;
  const SALE_TEXT = saleConfig.dealText;
  const PERCENTAGE = saleConfig.percentage;
  // A regional offer replaces the sale outright — it is forever, the sale is one-time.
  const parityOffer = useActiveParityOffer();
  const parityCopy = parityOffer && getParityOfferCopy(parityOffer);

  const couponCode = parityOffer ? parityOffer.couponCode : saleConfig.couponCode;

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  // The code swaps under us when the regional lookup resolves. Without this, a copy of the sale
  // code inside that window would keep announcing "copied" against the new code.
  useEffect(() => setCopied(false), [couponCode]);

  const handleCopy = () => {
    copyText(couponCode)
      .then(() => {
        setCopied(true);
        if (parityOffer) {
          trackEvent(PARITY_CODE_COPIED, {
            countryCode: parityOffer.countryCode,
            tierId: parityOffer.tierId,
            percentOff: parityOffer.percentOff,
            actionType: 'copy_code',
            route: typeof window === 'undefined' ? '' : window.location.pathname,
          });
        }
      })
      .catch(() => setCopied(false));
  };

  return (
    <div className="px-4">
      <div className="group relative mx-auto max-w-7xl">
        <div
          className={
            parityOffer
              ? 'absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-brand-green-primary via-electric-teal to-brand-green-secondary opacity-30 blur transition duration-1000 group-hover:opacity-50 group-hover:duration-200'
              : 'absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-yellow-400 via-orange-500 to-purple-600 opacity-30 blur transition duration-1000 group-hover:opacity-50 group-hover:duration-200'
          }
        ></div>
        <div className="relative flex flex-col items-center justify-between gap-2 overflow-hidden rounded-xl border border-white/10 bg-gray-900 p-6 shadow-2xl md:flex-row md:gap-8">
          <div className="flex flex-1 flex-col items-center space-y-2 text-center md:items-start md:space-y-3 md:text-left">
            <div className="flex items-center gap-2">
              {parityOffer && parityCopy ? (
                <span className="flex items-center gap-1.5 rounded border border-brand-green-primary/30 bg-brand-green-primary/10 px-2 py-0.5 text-xs font-bold uppercase tracking-widest text-brand-green-primary md:text-[10px]">
                  <CountryFlag
                    code={parityOffer.countryCode}
                    className="h-3 w-[1.05rem] rounded-[1px] object-cover ring-1 ring-brand-green-primary/30"
                  />
                  {parityCopy.badge}
                </span>
              ) : (
                <span className="rounded border border-yellow-500/20 bg-yellow-500/10 px-2 py-0.5 text-xs font-bold uppercase tracking-widest text-yellow-400 md:text-[10px]">
                  Limited Time Offer
                </span>
              )}
            </div>
            {parityOffer && parityCopy ? (
              <>
                <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-4xl">
                  <span className="bg-gradient-to-r from-brand-green-primary to-electric-teal bg-clip-text text-transparent">
                    {parityCopy.headline}
                  </span>
                </h2>
                <p className="text-sm text-white/70 md:text-base">{parityCopy.detail}</p>
              </>
            ) : (
              <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-4xl">
                <span className="mr-2 bg-gradient-to-r from-yellow-200 to-yellow-500 bg-clip-text text-transparent">
                  {PERCENTAGE} OFF
                </span>
                {SALE_TEXT}
              </h2>
            )}
          </div>
          <div className="flex w-full flex-shrink-0 flex-col gap-2 md:w-auto md:flex-row md:items-center md:gap-4">
            <div className="text-center text-lg font-semibold italic tracking-wide text-white/60 md:text-left md:text-2xl">
              {parityOffer ? 'Use code' : BUTTON_TEXT}
            </div>
            <div className="group/input relative flex-1 md:w-96">
              <div
                className={
                  parityOffer
                    ? 'absolute inset-0 rounded-lg bg-gradient-to-r from-brand-green-primary to-electric-teal opacity-20 blur transition group-hover/input:opacity-30'
                    : 'absolute inset-0 rounded-lg bg-gradient-to-r from-brand-primary to-purple-500 opacity-20 blur transition group-hover/input:opacity-30'
                }
              ></div>
              <div className="relative flex items-center rounded-lg border border-white/20 bg-[#0B0F19] p-2 shadow-inner">
                <div className="select-none border-r border-white/10 pl-2 pr-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 md:pl-3 md:pr-4 md:text-xs">
                  CODE
                </div>
                <div className="flex-1 text-center font-mono text-base font-bold tracking-widest text-white/80 selection:bg-yellow-500/30 md:text-xl">
                  {couponCode}
                </div>
                <button
                  onClick={handleCopy}
                  aria-label={`Copy coupon code ${couponCode}`}
                  className={
                    parityOffer
                      ? 'ml-2 rounded-md border border-brand-green-primary/30 bg-brand-green-secondary p-2 text-white shadow-lg shadow-emerald-600/20 transition-all hover:bg-brand-green-primary active:scale-95'
                      : 'ml-2 rounded-md border border-blue-400/20 bg-brand-primary p-2 text-white/80 shadow-lg shadow-blue-600/20 transition-all hover:bg-brand-primary active:scale-95'
                  }
                  title="Copy Code"
                >
                  {copied ? <FaCheck size={16} /> : <FaRegCopy size={16} />}
                </button>
              </div>
              {copied && (
                <div className="absolute -bottom-6 left-0 right-0 animate-pulse text-center text-xs font-medium text-emerald-400">
                  Code copied to clipboard!
                </div>
              )}
              <span className="sr-only" aria-live="polite">
                {copied ? `Coupon code ${couponCode} copied` : ''}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoButton;
